import { RequestAction, error } from '@redux-requests/core';
import { toast } from 'react-toastify';

import workflowMapper from 'shared/utils/api/mappers/workflowMapper';
import { WORKFLOWS_PATH } from 'shared/constants/routePaths';
import { convertObjectToQueryString } from 'shared/utils/api';
import { RawWorkflow, Workflow } from 'shared/types/workflow';
import { DestinationType, ApiCredentials } from 'shared/types/destination';
import { TransformationType } from 'shared/types/transformation';
import { mapToApiModeTypes } from 'shared/utils/api/mappers/types';

import customHistory from '../../customHistory';

import * as actions from './actions';
import { GetWorkflowApiCredentialsAction } from './types';

export const fetchWorkflows = (filters?: Object): RequestAction => {
  const queryString = convertObjectToQueryString(filters);

  return {
    type: actions.FETCH_WORKFLOWS,
    payload: {
      request: {
        url: `/services/workflows${queryString ? `?${queryString}` : ''}`,
      },
    },
    meta: {
      onSuccess: (response) => ({
        data: response.items.map(workflowMapper.mapFromApi),
      }),
    },
  };
};

// TODO: Improve the type!
export const handleWorkflowsFetch = (workflows: any) => ({
  type: actions.HANDLE_WORKFLOWS_FETCH,
  payload: { workflows },
});

export const getWorkflow = (workflowGuid: string): RequestAction => ({
  type: actions.GET_WORKFLOW,
  payload: {
    request: {
      url: `/services/workflows/${workflowGuid}`,
    },
  },
  meta: {
    onSuccess: (response: RawWorkflow) => ({
      data: workflowMapper.mapFromApi!(response),
    }),
    onError: () => {
      toast.error('The workflow has not been found.');
      customHistory.push(WORKFLOWS_PATH);
      throw error;
    },
  },
});

// TODO: Improve the type!
export const createWorkflow = (data: any): RequestAction => ({
  type: actions.CREATE_WORKFLOW,
  payload: {
    request: {
      data,
      url: '/services/workflows',
      method: 'post',
    },
  },
  meta: {
    onRequest: ({ data, ...rest }) => ({
      ...rest,
      body: workflowMapper.mapToApi!({
        name: data.details.name,
        type: data.details.type,
        sources: data.sources,
        transformations: data.transformations,
        destination: data.destination,
      }, mapToApiModeTypes.CREATE),
    }),
    onSuccess: (response) => ({
      data: workflowMapper.mapFromApi!(response),
    }),
  },
});

// TODO: Improve the type!
export const deleteWorkflow = (workflow: any) => ({
  type: actions.DELETE_WORKFLOW,
  payload: { workflow },
});

// TODO: Improve the `updatedValues` type!
export const editWorkflow = (workflow: Workflow, updatedValues: any): RequestAction => ({
  type: actions.EDIT_WORKFLOW,
  payload: {
    request: {
      updatedValues,
      url: `/services/workflows/${workflow.guid}`,
      method: 'put',
    },
  },
  meta: {
    onRequest: ({ updatedValues, ...rest }) => ({
      ...rest,
      body: workflowMapper.mapToApi!({
        name: updatedValues.details.name,
        guid: workflow.guid,
        type: updatedValues.details.type,
        sources: updatedValues.sources,
        transformations: updatedValues.transformations,
        destination: updatedValues.destination,
      }, mapToApiModeTypes.UPDATE),
    }),
    onSuccess: (response) => ({
      data: workflowMapper.mapFromApi!(response),
    }),
  },
});

export const getWorkflowApiCredentials = (
  workflowGuid: string,
  destinationType: DestinationType,
  regenerate?: boolean,
): GetWorkflowApiCredentialsAction => ({
  type: actions.GET_WORKFLOW_API_CREDENTIALS,
  payload: {
    workflowGuid,
    destinationType,
    regenerate,
  },
});

export const addApiCredentialsToWorkflow = (
  workflowGuid: string,
  apiCredentials: ApiCredentials,
) => ({
  type: actions.ADD_API_CREDENTIALS_TO_WORKFLOW,
  payload: {
    workflowGuid,
    apiCredentials,
  },
});
